import {
  useTheme,
  Box,
  Typography,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useResponseError } from "../../hooks/useResponseError";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getQuestionnaireAnswers,
  getQuestionnaireById,
  postQuestionnaireAnswers,
} from "../../services/questionnaireService";
import { Controller, FieldError, useForm } from "react-hook-form";
import { InputText } from "../../elements/InputText";
import RadioControl from "../../elements/RadioControl";
import { ErrorBox } from "../../elements/ErrorBox";
import { AlertIcon } from "../../elements/AlertIcon";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import { getJob } from "../../services/jobService";
dayjs.extend(utcPlugin);

const formatAnswer = (answers: any): string => {
  if (answers == null || answers === "") {
    return "—";
  }
  if (typeof answers === "string") {
    return answers;
  }
  if (typeof answers === "object" && answers.text) {
    return answers.text;
  }
  return String(answers);
};

const Detail = ({ label, value }: { label: string; value?: string | null }) => (
  <Box sx={{ mb: "16px" }}>
    <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B", mb: "4px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", lineHeight: "20px", whiteSpace: "pre-wrap" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const FillQuestionnaire = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isBooking = searchParams.get("booking") === "1";
  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();
  const [questionnaireFields, setQuestionnaireFields] = useState<any>();
  const [questionnaireFieldsAnswers, setQuestionnaireFieldsAnswers] =
    useState<any>();
  const [jobState, setJobState] = useState<any>();
  const [disabledFields, setDisabledFields] = useState<Record<string, boolean>>(
    {}
  );
  const [isRebook, setIsRebook] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [isSuccessResult, setSuccessResult] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const { responseError, setResponseError } = useResponseError(50000);
  const theme = useTheme();

  const parseAnswersContent = (content: any) => {
    if (!content) {
      return null;
    }
    if (typeof content === "string") {
      try {
        return JSON.parse(content);
      } catch {
        return null;
      }
    }
    return content;
  };

  const fetchAnswers = async (jobId: string) => {
    try {
      const res = await getQuestionnaireAnswers({
        id: jobId,
      });
      const parsed = parseAnswersContent(res?.content);
      return Array.isArray(parsed) ? parsed : null;
    } catch (error: unknown) {
      console.error("error", error);
      return null;
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    const load = async () => {
      setIsloading(true);
      try {
        const [jobRes, questionRes] = await Promise.all([
          getJob({ id }),
          getQuestionnaireById({ id }).catch(() => null),
        ]);

        setJobState(jobRes);
        setIsViewOnly(Boolean(jobRes?.questionnaireCompleted));
        setIsRebook(
          Boolean(
            jobRes?.rebooksJob &&
              jobRes?.category?.id === jobRes?.rebooksJob?.category?.id
          )
        );
        setQuestionnaireFields(
          questionRes?.content ? JSON.parse(questionRes.content) : null
        );

        let answers = await fetchAnswers(id);
        if (!answers?.length && jobRes?.rebooksJob?.id) {
          answers = await fetchAnswers(jobRes.rebooksJob.id);
        }
        setQuestionnaireFieldsAnswers(answers);
      } catch (error: unknown) {
        setResponseError((error as Error)?.message || "Something went wrong");
      } finally {
        setIsloading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    if (questionnaireFieldsAnswers && questionnaireFields) {
      questionnaireFieldsAnswers.forEach((field: any) => {
        const question = questionnaireFields.find(
          (que: any) => que.question === field.question
        );
        const answers = question?.options
          ? field?.answers?.id
          : field?.answers;
        setValue(question?.id, answers, { shouldTouch: true });
      });
    }
  }, [questionnaireFieldsAnswers, questionnaireFields, isRebook, setValue]);

  useEffect(() => {
    if (!isSuccessResult) {
      return;
    }
    const timer = setTimeout(
      () => {
        navigate("/dashboard", { replace: true });
      },
      isBooking ? 1500 : 0
    );
    return () => clearTimeout(timer);
  }, [isSuccessResult, isBooking, navigate]);

  const watchAllFields = watch();

  const onSubmit = async (data: any): Promise<any> => {
    const dataForReq = Object.keys(data).reduce((acc: any, item: any) => {
      if (item) {
        const questionField = questionnaireFields.find(
          (que: any) => que.id === item
        );
        if (questionField) {
          const question = questionField.question;
          const answer = questionField?.options
            ? questionField?.options.find((opt: any) => {
                return opt.id === data[item];
              })
            : data[item];

          if (answer) {
            const newItem = {
              question: question,
              answers: answer,
            };
            acc.push(newItem);
          }
        }
      }
      return acc;
    }, []);

    try {
      await postQuestionnaireAnswers({
        id: id || "",
        content: dataForReq,
      });

      setSuccessResult(true);
    } catch (error: unknown) {
      setResponseError((error as Error)?.message || "Something went wrong");
    }
  };

  const renderViewOnly = () => {
    const accessNotes = Array.isArray(questionnaireFieldsAnswers)
      ? questionnaireFieldsAnswers.filter(
          (item: any) => item?.question && !item?.isLabel
        )
      : [];

    return (
      <Box>
        <Detail
          label="Date"
          value={jobState?.startDate ? dayjs(jobState.startDate).format("MMMM D, YYYY") : "—"}
        />
        <Detail
          label="Time"
          value={
            jobState?.startDate && jobState?.endDate
              ? `${dayjs(jobState.startDate).format("h:mm A")} – ${dayjs(jobState.endDate).format("h:mm A")}`
              : "—"
          }
        />
        <Detail label="Service" value={jobState?.category?.name} />
        <Detail label="Address" value={jobState?.site?.address} />
        <Detail label="Service name" value={jobState?.name} />
        <Detail label="Notes" value={jobState?.description} />
        <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B", mb: "8px" }}>
          Access notes
        </Typography>
        {accessNotes.length ? (
          accessNotes.map((item: any, index: number) => (
            <Box key={`${item.question}-${index}`} sx={{ mb: "12px" }}>
              <Typography
                sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B", mb: "2px" }}
                dangerouslySetInnerHTML={{ __html: item.question }}
              />
              <Typography sx={{ fontSize: "14px", lineHeight: "20px", whiteSpace: "pre-wrap" }}>
                {formatAnswer(item.answers)}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
            No access notes from last time.
          </Typography>
        )}
      </Box>
    );
  };

  const renderFormFields = () => {
    if (!questionnaireFields && responseError) {
      return (
        <ErrorBox
          sx={{
            my: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          Can't get questionnaire
        </ErrorBox>
      );
    }

    return (
      <>
        {questionnaireFields && (
          <Box>
            {questionnaireFields.map((field: any, index: number) => {
              const isLastField = index === questionnaireFields.length - 1;
              const showField = field?.visibleWhenOptionIdIsActive
                ? Object.values(watchAllFields).some(
                    (item: any) =>
                      item && item.includes(field?.visibleWhenOptionIdIsActive)
                  )
                : true;

              if (field?.options) {
                return (
                  <FormControl
                    sx={{ mb: isLastField ? "12px" : "24px" }}
                    component="fieldset"
                    key={field.id + index}
                  >
                    <FormLabel
                      sx={{ mb: "16px" }}
                      focused={false}
                      color={"secondary"}
                      component="legend"
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: field.question }}
                        style={{ display: "contents" }}
                      />
                    </FormLabel>
                    <Controller
                      name={field.id}
                      control={control}
                      defaultValue=""
                      rules={{ required: "This field is required" }}
                      render={({ field: controllerField }) => (
                        <RadioGroup {...controllerField}>
                          {field.options.map((option: any) => (
                            <FormControlLabel
                              key={option.id}
                              value={option.id}
                              control={<RadioControl />}
                              label={option.text}
                              disabled={disabledFields[field.id]}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                    {errors[field.id] && (
                      <ErrorBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        {errors[field.id]?.message}
                      </ErrorBox>
                    )}
                  </FormControl>
                );
              } else if (field?.isLabel) {
                return (
                  <FormControl
                    sx={{ mb: "12px" }}
                    component="fieldset"
                    key={field.id + index}
                  >
                    <FormLabel
                      sx={{ mb: "8px" }}
                      focused={false}
                      color={"secondary"}
                      component="legend"
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: field.question }}
                        style={{ display: "contents" }}
                      />
                    </FormLabel>
                  </FormControl>
                );
              } else {
                return (
                  <FormControl
                    sx={{
                      display: showField ? "inline-flex" : "none",
                      mb: isLastField ? "12px" : "24px",
                      width: "100%",
                    }}
                    component="fieldset"
                    key={field.id + index}
                  >
                    <FormLabel
                      sx={{ mb: "16px" }}
                      color={"secondary"}
                      component="legend"
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: field.question }}
                        style={{ display: "contents" }}
                      />
                    </FormLabel>
                    <Controller
                      key={field.id}
                      name={field.id}
                      control={control}
                      rules={
                        field?.visibleWhenOptionIdIsActive
                          ? { required: false }
                          : {
                              required:
                                "This field is required. If not applicable, please type N/A.",
                            }
                      }
                      render={({ field: controllerField }) => (
                        <InputText
                          disabled={disabledFields[field.id]}
                          inputProps={{ ...controllerField }}
                          error={!!errors[field.name]}
                          placeholder="Description"
                          multiline
                          rows={2}
                          helperText={
                            errors[field.id]
                              ? (errors[field.id] as FieldError).message
                              : ""
                          }
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                );
              }
            })}
            {responseError && (
              <ErrorBox
                sx={{
                  my: "8px",
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                {responseError}
              </ErrorBox>
            )}
            {questionnaireFields && (
              <Box>
                {isBooking && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This questionnaire is required to finalize your booking. You can update answers if something changed.
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                >
                  {questionnaireFieldsAnswers ? "Update answers" : "Submit"}
                </Button>
                <Box
                  sx={{
                    mt: "32px",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    textAlign: "center",
                  }}
                >
                  <AlertIcon
                    sx={{ mb: "8px", width: "17px", height: "17px" }}
                  />
                  <Typography>
                    {isRebook
                      ? "Previous answers are filled in. Change anything that is different this time, then submit."
                      : "Double-check your answers before submitting. You can update them later if needed."}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={[
        { display: "flex", height: "100%" },
        (theme) => ({
          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
          },
        }),
      ]}
    >
      <Box
        sx={[
          {
            position: "relative",
            minHeight: "62px",
          },
        ]}
      >
        <Box
          sx={[
            { pt: "32px", mr: "12px" },
            (theme) => ({
              [theme.breakpoints.down("sm")]: {
                mr: 0,
                position: "absolute",
                left: "-20px",
                right: "-20px",
                py: "14px",
                px: "20px",
                borderBottom: "1px solid #DDDFDD",
              },
            }),
          ]}
        >
          <Button onClick={() => navigate(`/dashboard`)} variant="table">
            To Dashboard
          </Button>
        </Box>
      </Box>
      <Box
        sx={[
          {
            py: "96px",
            maxWidth: "570px",
            height: "100%",
            borderLeft: "1px solid #DDDFDD",
            borderRight: "1px solid #DDDFDD",
            width: "100%",
            margin: "auto",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flexDirection: "column",
            overflow: "auto",
          },
          (theme) => ({
            [theme.breakpoints.down("sm")]: {
              pt: "24px",
              pb: "50px",
              px: "10px",
              borderLeft: "0px",
              borderRight: "0px",
            },
          }),
        ]}
      >
        <Typography
          variant="h1"
          component={"h1"}
          sx={{
            mb: "32px",
          }}
        >
          Service {isViewOnly ? "details" : "questionnaire"}
        </Typography>
        <Box
          sx={{
            boxSizing: "border-box",
            maxWidth: "315px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
            }}
          >
            {isSuccessResult ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Questionnaire submitted successfully.
              </Alert>
            ) : isLoading ? (
              <Box
                sx={{
                  mt: "40px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : isViewOnly ? (
              <Box>{renderViewOnly()}</Box>
            ) : (
              <Box>{renderFormFields()}</Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FillQuestionnaire;
